import {
  CandidateFilterGroups,
  CandidateFilterState
} from 'modules/candidate-profile/types/CandidateSearchFilter';

// A chip group is one chip alone, or chips joined by Or. The rail joins chip
// groups with And: `A And (B Or C) And D`. Only the Or groups are stored.

const toIds = (chipGroups: CandidateFilterState[][]): CandidateFilterGroups =>
  chipGroups.map((chipGroup) => chipGroup.map((condition) => condition.id));

export const chipGroupsInOrder = (
  filters: CandidateFilterState[],
  groups: CandidateFilterGroups
): CandidateFilterState[][] => {
  const byId = new Map(filters.map((filter) => [filter.id, filter]));
  const positionOf = new Map(filters.map((filter, at) => [filter.id, at]));
  const inOrGroup = new Set<string>();

  const orGroups = groups.flatMap((group) => {
    const members = group
      .map((id) => byId.get(id))
      .filter((member): member is CandidateFilterState => Boolean(member));
    if (members.length < 2) return [];

    members.forEach((member) => inOrGroup.add(member.id));
    return [members];
  });

  const singles = filters
    .filter((filter) => !inOrGroup.has(filter.id))
    .map((filter) => [filter]);

  // A chip group goes where its first chip was.
  const firstPosition = (chipGroup: CandidateFilterState[]) =>
    Math.min(...chipGroup.map((chip) => positionOf.get(chip.id) ?? Infinity));

  return [...orGroups, ...singles].sort(
    (left, right) => firstPosition(left) - firstPosition(right)
  );
};

export const joinChipGroupsAt = (
  filters: CandidateFilterState[],
  groups: CandidateFilterGroups,
  groupIndex: number
): CandidateFilterGroups => {
  const chipGroups = toIds(chipGroupsInOrder(filters, groups));
  if (groupIndex < 0 || groupIndex + 1 >= chipGroups.length) return groups;

  chipGroups.splice(groupIndex, 2, [
    ...chipGroups[groupIndex],
    ...chipGroups[groupIndex + 1]
  ]);
  return chipGroups.filter((chipGroup) => chipGroup.length > 1);
};

export const splitChipGroupAt = (
  filters: CandidateFilterState[],
  groups: CandidateFilterGroups,
  groupIndex: number,
  chipIndex: number
): CandidateFilterGroups => {
  const chipGroups = toIds(chipGroupsInOrder(filters, groups));
  const chipGroup = chipGroups[groupIndex];
  if (!chipGroup || chipIndex < 0 || chipIndex + 1 >= chipGroup.length) {
    return groups;
  }

  chipGroups.splice(
    groupIndex,
    1,
    chipGroup.slice(0, chipIndex + 1),
    chipGroup.slice(chipIndex + 1)
  );
  return chipGroups.filter((each) => each.length > 1);
};

export const groupsAsConditions = (
  conditions: CandidateFilterState[],
  groups: CandidateFilterGroups
): CandidateFilterState[][] => {
  const byId = new Map(
    conditions.map((condition) => [condition.id, condition])
  );

  return groups.map((group) =>
    group
      .map((id) => byId.get(id))
      .filter((condition): condition is CandidateFilterState =>
        Boolean(condition)
      )
  );
};

// An Or group with one chip left is not an Or group.
export const removeMissingFromGroups = (
  filters: CandidateFilterState[],
  groups: CandidateFilterGroups
): CandidateFilterGroups => {
  const onRail = new Set(filters.map((filter) => filter.id));

  return groups
    .map((group) => group.filter((id) => onRail.has(id)))
    .filter((group) => group.length > 1);
};
